import { logPipelineStage, logPipelineError } from '../logging/pipelineLogger';
import { sendToTerminal } from '../../terminalLogger';
import { LayerService } from '../actions/layer';
import { writeSnapshot, setPreparedPipelineId } from './pipelineKeys';
import { PIPELINE_DELAYS, PIPELINE_TTL, PIPELINE_RETRY_CONFIG, pipelineSleep } from './pipelineConstants';
import { monitoradorObjetos } from '../MonitoradorObjetos';
import { executePipelineStepWithRetry } from './pipelineRetry';
import { FEATURE_FLAGS } from '../../../config/featureFlags';
import { corelCommunication } from '../communication';

// ✅ LOCK GLOBAL: Evita execução duplicada da pipeline (StrictMode monta componentes 2x)
let _pipelineRunning = false;
let _currentPipelineId: string | null = null;

/**
 * Verifica se há uma pipeline em execução
 */
export function isPipelineRunning(): boolean {
  return _pipelineRunning;
}

/**
 * Obtém o ID da pipeline em execução (ou null)
 */
export function getCurrentPipelineId(): string | null {
  return _currentPipelineId;
}

export type OrderPreparationStatus = 'success' | 'error' | 'cancelled';

export interface OrderPreparationOptions {
  pedidoId?: number | string;
  itemId?: number | string;
  scope?: 'all' | 'selection';
  overwrite?: boolean;
  renameOnConflict?: boolean;
  /** Pular etapa de distribuição por páginas (para orçamentos rápidos) */
  skipDistribute?: boolean;
  delaysMs?: { afterOrganize?: number; afterDistribute?: number; afterCollect?: number };
  timeoutsMs?: { organize?: number; distribute?: number; collect?: number };
  abortSignal?: AbortSignal | null;
  onProgress?: (event: { stage: string; meta?: Record<string, unknown> }) => void;
}

export interface OrderPreparationResult {
  status: OrderPreparationStatus;
  error?: { code: string; message?: string };
  data?: { objetos: any[] };
  metrics?: { organizeMs?: number; distributeMs?: number; collectMs?: number; monitorMs?: number; totalMs?: number };
  pipelineId?: string;
  docKey?: string | null;
}

/**
 * 🧪 TESTE: Tenta usar a action unificada do C# para pipeline de preparação
 * Se falhar ou flag desabilitado, retorna null para usar pipeline tradicional
 */
async function tryUnifiedPipelineAction(
  options: OrderPreparationOptions,
  pipelineId: string
): Promise<OrderPreparationResult | null> {
  // Verificar se flag está habilitado
  if (!FEATURE_FLAGS.USE_UNIFIED_PIPELINE_ACTION) {
    return null; // Usar pipeline tradicional
  }

  sendToTerminal('🧪 [PIPELINE] Tentando usar action unificada do C#', { 
    pipelineId,
    pedidoId: options.pedidoId,
    itemId: options.itemId 
  });

  const emit = (stage: string, meta?: Record<string, unknown>) => {
    try { if (typeof options.onProgress === 'function') options.onProgress({ stage, meta }); } catch {}
  };

  emit('unified:start');
  emit('unified:sending', { action: 'executar-pipeline' });

  try {
    // ✅ Log detalhado antes de chamar a action
    const actionPayload = {
      preset: 'organizar-distribuir-monitorar',
      options: {
        'organizar-camada': {
          scope: options.scope === 'selection' ? 'selection' : 'document',
          pageScope: 'document' // ✅ Garantir que organize todas as páginas do documento (não apenas página ativa isolada)
        },
        'distribuir-por-pagina': {
          adjustPageSize: true,
          createMissingPages: true,
          overwrite: options.overwrite ?? true, // ✅ Permitir redistribuir se já distribuído
          renameOnConflict: options.renameOnConflict ?? false
        }
      },
      includeStepResults: false
    };
    
    sendToTerminal('📤 [PIPELINE] Enviando action unificada do C#', {
      pipelineId,
      action: 'executar-pipeline',
      payload: actionPayload
    });

    emit('unified:waiting', { message: 'Aguardando resposta do CorelDRAW...' });

    // ✅ Chamar a nova action do C#
    const response = await new Promise<any>((resolve, reject) => {
      let resolved = false; // ✅ Proteção contra múltiplas chamadas do callback
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      
      const requestId = corelCommunication.sendActionGuarded(
        'executar-pipeline',
        actionPayload,
        (resp: any) => {
          // ✅ IGNORAR mensagens de progresso - aguardar resposta final
          if (resp?.type === 'progress') {
            // ✅ Log apenas para primeira e última etapa (reduzir poluição de logs)
            const etapaIndex = resp?.result?.etapaIndex;
            const totalEtapas = resp?.result?.totalEtapas;
            const isFirst = etapaIndex === 1;
            const isLast = etapaIndex === totalEtapas;
            
            if (isFirst || isLast) {
              sendToTerminal(`⏳ [PIPELINE] Progresso: ${resp?.result?.etapa} (${etapaIndex}/${totalEtapas})`, {
                pipelineId,
                etapa: resp?.result?.etapa,
                etapaIndex,
                totalEtapas,
                mensagem: resp?.message
              });
            }
            return; // ✅ Não processar, apenas aguardar próxima mensagem
          }

          // ✅ Proteção: garantir que callback só executa uma vez para resposta final
          if (resolved) {
            sendToTerminal('⚠️ [PIPELINE] Callback duplicado ignorado (resposta final já processada)', {
              pipelineId,
              requestId,
              status: resp?.status,
              action: resp?.action
            });
            return;
          }
          
          resolved = true;
          
          // ✅ Limpar timeout se resposta final chegou
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          sendToTerminal('📥 [PIPELINE] Resposta FINAL da action unificada recebida', {
            pipelineId,
            requestId,
            action: resp?.action,
            status: resp?.status,
            sucesso: resp?.result?.sucesso,
            mensagem: resp?.message || resp?.result?.mensagem,
            hasDados: !!resp?.result?.dados,
            objetosCount: resp?.result?.dados?.objetos?.length || 0
          });

          // ✅ Validação mais robusta e detalhada (conforme documentação)
          const actionOk = resp?.action === 'executar-pipeline';
          const statusOk = resp?.status === 'ok';
          const sucessoOk = resp?.result?.sucesso === true;
          const hasDados = !!resp?.result?.dados;
          const hasObjetos = Array.isArray(resp?.result?.dados?.objetos) && resp?.result?.dados?.objetos.length > 0;

          if (actionOk && statusOk && sucessoOk && hasDados && hasObjetos) {
            sendToTerminal('✅ [PIPELINE] Validação da resposta FINAL OK', {
              pipelineId,
              objetosCount: resp?.result?.dados?.objetos?.length,
              docKey: resp?.result?.dados?.docKey
            });
            resolve(resp);
          } else {
            const errorMsg = resp?.message || resp?.result?.mensagem || 'Action unificada retornou erro';
            sendToTerminal('❌ [PIPELINE] Action unificada retornou erro na resposta FINAL - VALIDAÇÃO DETALHADA', {
              pipelineId,
              validacoes: {
                actionOk,
                statusOk,
                sucessoOk,
                hasDados,
                hasObjetos
              },
              action: resp?.action,
              status: resp?.status,
              sucesso: resp?.result?.sucesso,
              mensagem: errorMsg,
              objetosCount: resp?.result?.dados?.objetos?.length || 0
            });
            
            // ✅ Log completo da resposta em log separado para não poluir
            sendToTerminal('📋 [PIPELINE] Resposta FINAL completa da action unificada (para debug)', {
              pipelineId,
              respostaCompleta: JSON.stringify(resp, null, 2)
            });
            reject(new Error(errorMsg));
          }
        },
        60000 // Timeout de 60s
      );

      if (!requestId) {
        sendToTerminal('❌ [PIPELINE] Falha ao enviar action unificada - requestId é null', {
          pipelineId,
          action: 'executar-pipeline'
        });
        reject(new Error('Falha ao enviar action unificada (sessão ausente ou WebView indisponível)'));
        return;
      }

      sendToTerminal('✅ [PIPELINE] Action unificada enviada com sucesso', {
        pipelineId,
        requestId
      });

      // ✅ Timeout de segurança (65s - maior que o timeout da action)
      timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true; // Marcar como resolvido para evitar chamadas duplicadas
          sendToTerminal('⏱️ [PIPELINE] Timeout na action unificada (65s)', {
            pipelineId,
            requestId
          });
          emit('unified:timeout', { timeoutMs: 65000 });
          reject(new Error('Timeout na action unificada (65s)'));
        }
      }, 65000);
    });

    // ✅ Resposta recebida - processando
    emit('unified:processing', { message: 'Processando resposta e preparando dados...' });

    // ✅ Extrair dados da resposta
    const dados = response?.result?.dados;
    if (!dados || !Array.isArray(dados.objetos) || dados.objetos.length === 0) {
      throw new Error('Resposta da action unificada não contém objetos válidos');
    }

    const objetos = dados.objetos;
    const docKey = dados.docKey || dados.documentoAtivo || null;
    const capturedAt = Date.now();
    const tempoTotalMs = response?.result?.resumo?.tempoTotalMs || 0;

    // ✅ IMPORTANTE: Registrar docKey no MonitoradorObjetos para proteção contra eventos antigos
    // Com a pipeline unificada, o monitor é iniciado pelo C#, então precisamos registrar
    // o docKey aqui para que a proteção de estabilização funcione corretamente
    if (docKey && typeof docKey === 'string') {
      try {
        monitoradorObjetos.registrarDocKey(docKey, '🔑 [PIPELINE] docKey da pipeline unificada');
        sendToTerminal('🔑 [PIPELINE] docKey registrado no MonitoradorObjetos (pipeline unificada)', {
          pipelineId,
          docKey
        });
      } catch (err) {
        sendToTerminal('⚠️ [PIPELINE] Erro ao registrar docKey no MonitoradorObjetos', {
          pipelineId,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }

    sendToTerminal('✅ [PIPELINE] Action unificada concluída com sucesso', {
      pipelineId,
      objetosCount: objetos.length,
      docKey,
      tempoTotalMs
    });

    emit('unified:success', { objetosCount: objetos.length, tempoTotalMs });
    emit('unified:persisting', { message: 'Salvando snapshot dos objetos...' });

    // ✅ Persistir snapshot (mesma lógica da pipeline tradicional)
    try {
      const payload = {
        meta: {
          docKey,
          capturedAt,
          total: objetos.length
        },
        objetos
      };
      
      writeSnapshot(pipelineId, payload, PIPELINE_TTL.SNAPSHOT_EXTENDED);
      
      if (options.pedidoId && options.itemId) {
        setPreparedPipelineId(options.pedidoId, options.itemId, pipelineId);

        // ✅ Gravar flag de distribuição (compatível com SalesOrdersPage)
        try {
          sessionStorage.setItem(`corel:dist:v1:${options.pedidoId}:${options.itemId}`, '1');
        } catch {}
      } else {
        // ⚠️ Sem (pedidoId, itemId) a flag preparada não é gravada e a janela NUNCA hidratará
        // este snapshot. Isso indica chamada com pedido null/incompleto — ver guard em
        // SalesOrdersPage.handleShowDetails.
        sendToTerminal('⚠️ [PIPELINE] Snapshot gravado SEM flag preparada — pedidoId/itemId ausentes', {
          pipelineId,
          pedidoId: options.pedidoId ?? null,
          itemId: options.itemId ?? null,
        });
      }

      sendToTerminal('💾 [PIPELINE] Snapshot persistido da action unificada', {
        pipelineId,
        objetosCount: objetos.length,
        ttlMs: PIPELINE_TTL.SNAPSHOT_EXTENDED
      });

      emit('snapshot:persisted', { count: objetos.length });
    } catch (err) {
      sendToTerminal('⚠️ [PIPELINE] Erro ao persistir snapshot da action unificada', {
        error: err instanceof Error ? err.message : String(err)
      });
      // Não falhar a pipeline por erro de persistência, mas logar
    }

    // ✅ Retornar resultado no formato esperado
    const result: OrderPreparationResult = {
      status: 'success',
      data: {
        objetos
      },
      pipelineId,
      docKey,
      metrics: {
        totalMs: tempoTotalMs
      }
    };

    emit('end', { ok: true });
    return result;

  } catch (error) {
    // ✅ Log detalhado do erro
    const errorMsg = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    
    sendToTerminal('❌ [PIPELINE] Action unificada falhou - detalhes do erro', {
      error: errorMsg,
      stack: errorStack,
      pipelineId
    });
    
    // 🧹 Limpar monitor que pode ter sido iniciado pela action unificada
    try {
      const preCheckStatus = await monitoradorObjetos.verificarStatus();
      if (preCheckStatus?.ativo === true) {
        sendToTerminal('🛑 [PIPELINE] Parando monitor iniciado pela action unificada', { pipelineId });
        const stopResult = await monitoradorObjetos.stopWithRetry(3, 2000);
        if (stopResult.success) {
          await pipelineSleep(500, 'after_unified_action_cleanup');
          sendToTerminal('✅ [PIPELINE] Monitor limpo após falha da action unificada', { pipelineId });
        } else {
          sendToTerminal('⚠️ [PIPELINE] Não foi possível parar monitor após falha da action unificada', {
            pipelineId,
            error: stopResult.error
          });
        }
      } else {
        sendToTerminal('ℹ️ [PIPELINE] Monitor não estava ativo após falha da action unificada', { pipelineId });
      }
    } catch (cleanupError) {
      sendToTerminal('⚠️ [PIPELINE] Erro ao limpar monitor após falha da action unificada', {
        pipelineId,
        error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError)
      });
    }
    
    // ✅ Quando flag está habilitada, NÃO fazer fallback - retornar null para que a pipeline retorne erro
    sendToTerminal('❌ [PIPELINE] Action unificada falhou - SEM fallback (flag habilitado)', {
      error: errorMsg,
      pipelineId
    });
    emit('unified:error', { error: errorMsg });
    return null; // Retorna null para que a pipeline principal retorne erro ao invés de fazer fallback
  }
}

export async function runOrderPreparationPipeline(options: OrderPreparationOptions = {}): Promise<OrderPreparationResult> {
  const startedAt = Date.now();
  const pipelineId = `pipe-${startedAt}-${Math.random().toString(36).slice(2, 7)}`;
  
  // ✅ LOCK GLOBAL: Verificar se já existe pipeline em execução
  if (_pipelineRunning) {
    sendToTerminal('⏭️ [PIPELINE] SKIP - já existe pipeline em execução', { 
      newPipelineId: pipelineId,
      currentPipelineId: _currentPipelineId 
    });
    return { 
      status: 'cancelled', 
      error: { code: 'already_running', message: 'Já existe uma pipeline em execução' },
      metrics: { totalMs: 0 },
      pipelineId 
    };
  }
  
  // ✅ Ativar lock global
  _pipelineRunning = true;
  _currentPipelineId = pipelineId;
  
  const delays = {
    afterOrganize: options?.delaysMs?.afterOrganize ?? PIPELINE_DELAYS.AFTER_ORGANIZE,
    afterDistribute: options?.delaysMs?.afterDistribute ?? PIPELINE_DELAYS.AFTER_DISTRIBUTE_UI_STABILIZATION, // ✅ Usar delay de 1000ms
  };
  const scope = options.scope ?? 'all';

  const emit = (stage: string, meta?: Record<string, unknown>) => {
    try { if (typeof options.onProgress === 'function') options.onProgress({ stage, meta }); } catch {}
  };
  
  // ✅ Log do modo ativo
  const modoAtivo = FEATURE_FLAGS.USE_UNIFIED_PIPELINE_ACTION ? 'ACTION UNIFICADA (C#)' : 'PIPELINE TRADICIONAL';
  sendToTerminal(`🔧 [PIPELINE] Modo ativo: ${modoAtivo}`, { 
    pipelineId, 
    pedidoId: options.pedidoId, 
    itemId: options.itemId,
    flagHabilitado: FEATURE_FLAGS.USE_UNIFIED_PIPELINE_ACTION
  });

  sendToTerminal('━━━━━━ [PIPELINE] INÍCIO ━━━━━━', { pipelineId, pedidoId: options.pedidoId, itemId: options.itemId });
  logPipelineStage(pipelineId, 'START', { pedidoId: options.pedidoId, itemId: options.itemId, modo: modoAtivo });
  emit('start', { pipelineId, pedidoId: options.pedidoId, itemId: options.itemId });

  // ✅ Helper para liberar lock ao retornar
  const releaseLockAndReturn = (result: OrderPreparationResult): OrderPreparationResult => {
    _pipelineRunning = false;
    _currentPipelineId = null;
    return result;
  };

  // Verificar abortSignal antes de iniciar
  if (options.abortSignal?.aborted) {
    sendToTerminal('❌ [PIPELINE] ABORTADO antes de iniciar', { pipelineId });
    logPipelineError(pipelineId, 'ABORTED', { stage: 'before_start' });
    return releaseLockAndReturn({ status: 'cancelled', error: { code: 'aborted' }, metrics: { totalMs: Date.now() - startedAt } });
  }

  // 🧪 NOVO: Tentar action unificada primeiro (se flag habilitado)
  if (FEATURE_FLAGS.USE_UNIFIED_PIPELINE_ACTION) {
    sendToTerminal('🚀 [PIPELINE] Modo ACTION UNIFICADA - ignorando pipeline tradicional', {
      pipelineId,
      pedidoId: options.pedidoId,
      itemId: options.itemId
    });
    
    const unifiedResult = await tryUnifiedPipelineAction(options, pipelineId);
    
    if (unifiedResult) {
      // ✅ Sucesso! Liberar lock e retornar
      sendToTerminal('━━━━━━ [PIPELINE] SUCESSO (ACTION UNIFICADA) ━━━━━━', {
        pipelineId,
        objetosCount: unifiedResult.data?.objetos?.length || 0,
        docKey: unifiedResult.docKey,
        metrics: unifiedResult.metrics
      });
      logPipelineStage(pipelineId, 'FINISH', { via: 'unified_action' });
      return releaseLockAndReturn(unifiedResult);
    } else {
      // ❌ Action unificada falhou - NÃO fazer fallback, retornar erro
      sendToTerminal('❌ [PIPELINE] Action unificada falhou - NÃO usando fallback (flag habilitado)', {
        pipelineId,
        pedidoId: options.pedidoId,
        itemId: options.itemId
      });
      logPipelineError(pipelineId, 'UNIFIED_ACTION_FAILED', { 
        code: 'unified_action_failed',
        message: 'Action unificada falhou e fallback está desabilitado quando flag está ativo'
      });
      return releaseLockAndReturn({
        status: 'error',
        error: {
          code: 'unified_action_failed',
          message: 'A action unificada do C# falhou. Verifique os logs para mais detalhes.'
        },
        metrics: { totalMs: Date.now() - startedAt },
        pipelineId
      });
    }
  }

  // ✅ Se flag desabilitado, usar pipeline tradicional

  // ═══════════════════════════════════════════════════════════════════════════
  // 🛑 ETAPA 0: PARAR MONITOR ANTERIOR (se ativo) - silencioso
  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ Igual productionOpenPipeline.ts - evita erro "Monitoramento já está ativo"
  try {
    const preCheckStatus = await monitoradorObjetos.verificarStatus();
    const monitorAtivo = preCheckStatus?.ativo === true;
    
    if (monitorAtivo) {
      sendToTerminal('🛑 [PIPELINE:0] Parando monitor anterior', { pipelineId });
      
      const stopResult = await monitoradorObjetos.stopWithRetry(3, 2000);
      
      if (!stopResult.success) {
        sendToTerminal('⚠️ [PIPELINE:0] Falha ao parar monitor anterior (continuando)', { 
          pipelineId,
          error: stopResult.error 
        });
      } else {
        // Aguardar estabilização após parar
        await pipelineSleep(500, 'after_monitor_stop');
      }
    }
  } catch (err) {
    sendToTerminal('⚠️ [PIPELINE:0] Erro ao verificar/parar monitor (continuando)', {
      pipelineId,
      error: err instanceof Error ? err.message : String(err)
    });
  }

  // Verificar abort após etapa 0
  if (options.abortSignal?.aborted) {
    sendToTerminal('❌ [PIPELINE] ABORTADO após etapa 0', { pipelineId });
    logPipelineError(pipelineId, 'ABORTED', { stage: 'after_stop_monitor' });
    return releaseLockAndReturn({ status: 'cancelled', error: { code: 'aborted' }, metrics: { totalMs: Date.now() - startedAt } });
  }

  // 🎨 ETAPA 1: ORGANIZAR CAMADAS (COM RETRY)
  sendToTerminal('🎨 [PIPELINE:1] Iniciando organização de camadas...', { pipelineId });
  const t1 = Date.now();
  emit('organize:send');
  
  // Verificar abortSignal antes de organizar
  if (options.abortSignal?.aborted) {
    sendToTerminal('❌ [PIPELINE] ABORTADO antes de organizar', { pipelineId });
    logPipelineError(pipelineId, 'ABORTED', { stage: 'before_organize' });
    return releaseLockAndReturn({ status: 'cancelled', error: { code: 'aborted' }, metrics: { totalMs: Date.now() - startedAt } });
  }
  
  let org: { ok: boolean; objetos?: Array<{ StaticID: string; Guid: string }>; error?: string };
  let organizeMs: number;
  
  try {
    const retryResult = await executePipelineStepWithRetry(
      () => LayerService.organizarCamadaAwait(),
      {
        maxRetries: PIPELINE_RETRY_CONFIG.DEFAULT_MAX_RETRIES,
        baseDelayMs: PIPELINE_RETRY_CONFIG.DEFAULT_BASE_DELAY_MS,
        maxDelayMs: PIPELINE_RETRY_CONFIG.DEFAULT_MAX_DELAY_MS,
        backoffFactor: PIPELINE_RETRY_CONFIG.DEFAULT_BACKOFF_FACTOR,
        validateResult: (result) => result?.ok === true,
        abortSignal: options.abortSignal,
        stepName: 'organizar-camada',
        pipelineId,
        onRetry: (attempt) => {
          emit('organize:retry', { attempt });
        }
      }
    );
    
    org = retryResult.result;
    organizeMs = retryResult.totalMs;
    
    sendToTerminal('✅ [PIPELINE:1] Camadas organizadas com sucesso', {
      pipelineId,
      durationMs: organizeMs,
      objetosCount: org.objetos?.length ?? 0,
      attempts: retryResult.attempts
    });
    
    emit('organize:ok', { durationMs: organizeMs, count: org.objetos?.length ?? 0, attempts: retryResult.attempts });
  } catch (error) {
    organizeMs = Date.now() - t1;
    const errorMsg = error instanceof Error ? error.message : 'CorelDRAW não respondeu ou retornou erro durante a organização de camadas';
    
    sendToTerminal('❌ [PIPELINE:1] Falha ao organizar camadas após retries', {
      pipelineId,
      durationMs: organizeMs,
      error: errorMsg
    });
    
    logPipelineError(pipelineId, 'ORGANIZAR', { code: 'organize_failed', message: errorMsg });
    emit('organize:error', { durationMs: organizeMs, error: errorMsg });
    
    return releaseLockAndReturn({ 
      status: 'error', 
      error: { code: 'organize_failed', message: errorMsg }, 
      metrics: { organizeMs, totalMs: Date.now() - startedAt } 
    });
  }
  
  sendToTerminal(`⏱️ [PIPELINE:1] Sleep ${delays.afterOrganize}ms (após organizar)`, { pipelineId });
  await pipelineSleep(delays.afterOrganize, 'after_organize');

  // Variáveis de métricas da distribuição
  let distributeMs = 0;

  // ═══════════════════════════════════════════════════════════════════════════
  // 📄 ETAPA 2: DISTRIBUIR OBJETOS POR PÁGINAS (OPCIONAL - skipDistribute)
  // ═══════════════════════════════════════════════════════════════════════════
  if (!options.skipDistribute) {
    // Verificar abortSignal antes de distribuir
    if (options.abortSignal?.aborted) {
      sendToTerminal('❌ [PIPELINE] ABORTADO antes de distribuir', { pipelineId });
      logPipelineError(pipelineId, 'ABORTED', { stage: 'before_distribute' });
      return releaseLockAndReturn({ status: 'cancelled', error: { code: 'aborted' }, metrics: { organizeMs, totalMs: Date.now() - startedAt } });
    }

    sendToTerminal('📄 [PIPELINE:2] Iniciando distribuição de objetos por páginas...', { pipelineId, scope });
    const t2 = Date.now();
    emit('distribute:send', { scope, mode: 'auto', renameOnConflict: true });
    
    try {
      const retryResult = await executePipelineStepWithRetry(
        () => LayerService.distribuirPorPagina({ scope, mode: 'auto', renameOnConflict: true }),
        {
          maxRetries: PIPELINE_RETRY_CONFIG.DEFAULT_MAX_RETRIES,
          baseDelayMs: PIPELINE_RETRY_CONFIG.DEFAULT_BASE_DELAY_MS,
          maxDelayMs: PIPELINE_RETRY_CONFIG.DEFAULT_MAX_DELAY_MS,
          backoffFactor: PIPELINE_RETRY_CONFIG.DEFAULT_BACKOFF_FACTOR,
          validateResult: (result) => result === true,
          abortSignal: options.abortSignal,
          stepName: 'distribuir-por-pagina',
          pipelineId,
          onRetry: (attempt) => {
            emit('distribute:retry', { attempt });
          }
        }
      );
      
      distributeMs = retryResult.totalMs;
      
      sendToTerminal('✅ [PIPELINE:2] Objetos distribuídos com sucesso', {
        pipelineId,
        durationMs: distributeMs,
        attempts: retryResult.attempts
      });
      
      emit('distribute:ok', { durationMs: distributeMs, attempts: retryResult.attempts });
    } catch (error) {
      distributeMs = Date.now() - t2;
      const errorMsg = error instanceof Error ? error.message : 'Não foi possível distribuir os objetos por páginas. Verifique se o documento possui objetos válidos.';
      
      sendToTerminal('❌ [PIPELINE:2] Falha ao distribuir objetos após retries', {
        pipelineId,
        durationMs: distributeMs,
        error: errorMsg
      });
      
      logPipelineError(pipelineId, 'DISTRIBUIR', { code: 'distribute_failed', message: errorMsg });
      emit('distribute:error', { durationMs: distributeMs, error: errorMsg });
      
      return releaseLockAndReturn({ 
        status: 'error', 
        error: { code: 'distribute_failed', message: errorMsg }, 
        metrics: { organizeMs, distributeMs, totalMs: Date.now() - startedAt } 
      });
    }
    
    sendToTerminal(`⏱️ [PIPELINE:2] Sleep ${delays.afterDistribute}ms (estabilização UI do CorelDRAW)`, { pipelineId });
    await pipelineSleep(delays.afterDistribute, 'after_distribute');
  } else {
    // ✅ Skip distribuição (orçamentos rápidos)
    sendToTerminal('⏭️ [PIPELINE:2] SKIP - Distribuição pulada (skipDistribute=true)', { pipelineId });
    emit('distribute:skipped');
  }

  // (Removido) Etapa de metadados-objetos (collect)

  // 👀 ETAPA 3: MONITORAR OBJETOS (INICIAR)
  sendToTerminal('👀 [PIPELINE:3] Iniciando monitoramento de objetos...', { pipelineId });
  const t4 = Date.now();
  let monitorInitOk = false;
  let monitorStatusOk = false;
  let pinnedDocKey: string | null = null;
  let syncedObjetos: any[] = []; // ✅ Declarar FORA do try para estar acessível no retorno final
  try {
    emit('monitor:start', { pollingInterval: 400, batchSize: 10, pin: false });
    
    // Iniciar monitor usando MonitoradorObjetos (COM RETRY)
    let inicioResult: any;
    let ok = false;
    
    try {
      const retryResult = await executePipelineStepWithRetry(
        () => monitoradorObjetos.iniciarComConfiguracao({
          pollingInterval: 400,
          batchSize: 10,
          snapshotPaginado: false,
          pin: false // ✅ Segue ActiveDocument (compatível com SalesOrdersPage)
        }),
        {
          maxRetries: PIPELINE_RETRY_CONFIG.DEFAULT_MAX_RETRIES,
          baseDelayMs: PIPELINE_RETRY_CONFIG.DEFAULT_BASE_DELAY_MS,
          maxDelayMs: PIPELINE_RETRY_CONFIG.DEFAULT_MAX_DELAY_MS,
          backoffFactor: PIPELINE_RETRY_CONFIG.DEFAULT_BACKOFF_FACTOR,
          validateResult: (result) => result?.status === 'ok' && result?.result?.sucesso === true,
          abortSignal: options.abortSignal,
          stepName: 'monitorar-objetos-iniciar',
          pipelineId,
          onRetry: (attempt) => {
            emit('monitor:start_retry', { attempt });
          }
        }
      );
      
      inicioResult = retryResult.result;
      
      sendToTerminal('📥 [PIPELINE:3] Resposta do INICIAR recebida', {
        pipelineId,
        status: inicioResult?.status,
        resultSucesso: inicioResult?.result?.sucesso,
        resultMensagem: inicioResult?.result?.mensagem,
        hasData: !!inicioResult?.result?.data,
        attempts: retryResult.attempts
      });
      
      // Validar usando adapter (novo formato)
      ok = inicioResult?.status === 'ok' && inicioResult?.result?.sucesso === true;
      monitorInitOk = !!ok;
      
      // 🚧 GATE CRÍTICO ETAPA 3: Bloquear imediatamente se INICIAR falhar
      if (!monitorInitOk) {
        sendToTerminal('❌ [PIPELINE:3] GATE CRÍTICO - Monitor não iniciado, abortando pipeline', {
          pipelineId,
          monitorInitOk,
          status: inicioResult?.status,
          sucesso: inicioResult?.result?.sucesso,
          mensagem: inicioResult?.result?.mensagem
        });
        logPipelineError(pipelineId, 'MONITOR_INIT_FAILED', { 
          code: 'monitor_init_failed',
          status: inicioResult?.status,
          sucesso: inicioResult?.result?.sucesso,
          mensagem: inicioResult?.result?.mensagem
        });
        const errorMsg = inicioResult?.result?.mensagem || 'Monitor não pôde ser iniciado. Verifique se o CorelDRAW está respondendo corretamente.';
        return releaseLockAndReturn({ 
          status: 'error', 
          error: { code: 'monitor_init_failed', message: errorMsg }, 
          metrics: { organizeMs, distributeMs, totalMs: Date.now() - startedAt } 
        });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Falha ao iniciar monitoramento após retries';
      sendToTerminal('❌ [PIPELINE:3] Falha ao INICIAR monitor após retries', {
        pipelineId,
        error: errorMsg
      });
      monitorInitOk = false;
      ok = false;
      
      // 🚧 GATE CRÍTICO ETAPA 3: Bloquear imediatamente se INICIAR falhar (exceção)
      sendToTerminal('❌ [PIPELINE:3] GATE CRÍTICO - Exceção ao iniciar monitor, abortando pipeline', {
        pipelineId,
        monitorInitOk,
        error: errorMsg
      });
      logPipelineError(pipelineId, 'MONITOR_INIT_EXCEPTION', { 
        code: 'monitor_init_exception',
        error: errorMsg
      });
      return releaseLockAndReturn({ 
        status: 'error', 
        error: { code: 'monitor_init_failed', message: errorMsg }, 
        metrics: { organizeMs, distributeMs, totalMs: Date.now() - startedAt } 
      });
    }
    
    try {
      // Dados agora vêm de result.data (novo formato)
      const docKeyAck = inicioResult?.result?.data?.docKey;
      if (typeof docKeyAck === 'string') {
        pinnedDocKey = String(docKeyAck);
        sendToTerminal('🔑 [PIPELINE:3] docKey extraído', { pipelineId, docKey: pinnedDocKey });
      }
    } catch (err) {
      sendToTerminal('⚠️ [PIPELINE:3] Erro ao extrair docKey', {
        pipelineId,
        error: err instanceof Error ? err.message : String(err)
      });
      logPipelineError(pipelineId, 'MONITOR_INIT_DOCKEY', { 
        error: err instanceof Error ? err.message : String(err) 
      });
    }
    
    sendToTerminal(ok ? '✅ [PIPELINE:3] Monitor INICIADO com sucesso' : '❌ [PIPELINE:3] Falha ao INICIAR monitor', {
      pipelineId,
      monitorInitOk
    });
    
    emit(ok ? 'monitor:ack' : 'monitor:ack_error', { 
      status: inicioResult?.status, 
      sucesso: inicioResult?.result?.sucesso,
      message: inicioResult?.result?.mensagem 
    });
    
    // Status após delay configurado (sequência definida)
    sendToTerminal(`⏱️ [PIPELINE:3] Sleep ${PIPELINE_DELAYS.AFTER_MONITOR_INIT}ms (após iniciar monitor)`, { pipelineId });
    await pipelineSleep(PIPELINE_DELAYS.AFTER_MONITOR_INIT, 'after_monitor_init');
    
    // 🔍 ETAPA 4: VERIFICAR STATUS DO MONITOR (COM RETRY)
    sendToTerminal('🔍 [PIPELINE:4] Verificando status do monitor...', { pipelineId });
    emit('monitor:status_send');
    
    let statusResult: any;
    try {
      const retryResult = await executePipelineStepWithRetry(
        () => monitoradorObjetos.verificarStatus(),
        {
          maxRetries: PIPELINE_RETRY_CONFIG.DEFAULT_MAX_RETRIES,
          baseDelayMs: PIPELINE_RETRY_CONFIG.DEFAULT_BASE_DELAY_MS,
          maxDelayMs: PIPELINE_RETRY_CONFIG.DEFAULT_MAX_DELAY_MS,
          backoffFactor: PIPELINE_RETRY_CONFIG.DEFAULT_BACKOFF_FACTOR,
          validateResult: (result) => result !== null && result !== undefined,
          abortSignal: options.abortSignal,
          stepName: 'monitorar-objetos-status',
          pipelineId,
          onRetry: (attempt) => {
            emit('monitor:status_retry', { attempt });
          }
        }
      );
      
      statusResult = retryResult.result;
      
      sendToTerminal('📥 [PIPELINE:4] Resposta do STATUS recebida', {
        pipelineId,
        hasResult: !!statusResult,
        ativo: statusResult?.ativo,
        objetosMonitorados: statusResult?.objetosMonitorados,
        documentoDisponivel: statusResult?.documentoDisponivel,
        docKey: statusResult?.docKey,
        attempts: retryResult.attempts
      });
      
      // statusResult agora é MonitorStatusData diretamente
      if (statusResult) {
        monitorStatusOk = statusResult.ativo === true;
      }
      
      // 🚧 GATE CRÍTICO ETAPA 4: Bloquear imediatamente se STATUS falhar ou monitor não estiver ativo
      if (!statusResult || !monitorStatusOk) {
        sendToTerminal('❌ [PIPELINE:4] GATE CRÍTICO - Monitor não está ativo, abortando pipeline', {
          pipelineId,
          monitorStatusOk,
          hasResult: !!statusResult,
          ativo: statusResult?.ativo
        });
        logPipelineError(pipelineId, 'MONITOR_STATUS_FAILED', { 
          code: 'monitor_status_failed',
          hasResult: !!statusResult,
          ativo: statusResult?.ativo
        });
        const errorMsg = !statusResult 
          ? 'Não foi possível verificar o status do monitoramento. O CorelDRAW pode estar ocupado ou instável.'
          : 'O monitoramento não está ativo. Verifique se o CorelDRAW está respondendo corretamente.';
        return releaseLockAndReturn({ 
          status: 'error', 
          error: { code: 'monitor_status_failed', message: errorMsg }, 
          metrics: { organizeMs, distributeMs, monitorMs: Date.now() - t4, totalMs: Date.now() - startedAt } 
        });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Falha ao verificar status após retries';
      sendToTerminal('❌ [PIPELINE:4] Falha ao verificar status após retries', {
        pipelineId,
        error: errorMsg
      });
      statusResult = null;
      monitorStatusOk = false;
      
      // 🚧 GATE CRÍTICO ETAPA 4: Bloquear imediatamente se STATUS falhar (exceção)
      sendToTerminal('❌ [PIPELINE:4] GATE CRÍTICO - Exceção ao verificar status, abortando pipeline', {
        pipelineId,
        monitorStatusOk,
        error: errorMsg
      });
      logPipelineError(pipelineId, 'MONITOR_STATUS_EXCEPTION', { 
        code: 'monitor_status_exception',
        error: errorMsg
      });
      return releaseLockAndReturn({ 
        status: 'error', 
        error: { code: 'monitor_status_failed', message: errorMsg }, 
        metrics: { organizeMs, distributeMs, monitorMs: Date.now() - t4, totalMs: Date.now() - startedAt } 
      });
    }
    
    if (statusResult) {
      
      try {
        const docKeySt = statusResult?.docKey;
        if (!pinnedDocKey && typeof docKeySt === 'string') {
          pinnedDocKey = String(docKeySt);
          sendToTerminal('🔑 [PIPELINE:4] docKey extraído do status', { pipelineId, docKey: pinnedDocKey });
        }
      } catch (err) {
        sendToTerminal('⚠️ [PIPELINE:4] Erro ao extrair docKey do status', {
          pipelineId,
          error: err instanceof Error ? err.message : String(err)
        });
        logPipelineError(pipelineId, 'MONITOR_STATUS_DOCKEY', { 
          error: err instanceof Error ? err.message : String(err) 
        });
      }
      
      sendToTerminal(monitorStatusOk ? '✅ [PIPELINE:4] Monitor está ATIVO' : '⚠️ [PIPELINE:4] Monitor está INATIVO', {
        pipelineId,
        monitorStatusOk
      });
      
      emit('monitor:status_recv', { ativo: statusResult.ativo, result: statusResult });
    } else {
      sendToTerminal('❌ [PIPELINE:4] Status retornou NULL', { pipelineId });
    }

    // Sync após status para snapshot inicial completo (aguardar delay adicional)
    sendToTerminal(`⏱️ [PIPELINE:4] Sleep ${PIPELINE_DELAYS.AFTER_MONITOR_STATUS}ms (após verificar status)`, { pipelineId });
    await pipelineSleep(PIPELINE_DELAYS.AFTER_MONITOR_STATUS, 'after_monitor_status');
    
    // 🔄 ETAPA 5: SINCRONIZAR OBJETOS (COM RETRY)
    sendToTerminal('🔄 [PIPELINE:5] Iniciando SYNC de objetos...', { pipelineId });
    let syncedOk = false;
    // syncedObjetos já foi declarado fora do try (linha ~287)
    
    emit('monitor:sync_send');
    
    let syncResult: any;
    try {
      const retryResult = await executePipelineStepWithRetry(
        () => monitoradorObjetos.sincronizarObjetos(),
        {
          maxRetries: PIPELINE_RETRY_CONFIG.DEFAULT_MAX_RETRIES,
          baseDelayMs: PIPELINE_RETRY_CONFIG.DEFAULT_BASE_DELAY_MS,
          maxDelayMs: PIPELINE_RETRY_CONFIG.DEFAULT_MAX_DELAY_MS,
          backoffFactor: PIPELINE_RETRY_CONFIG.DEFAULT_BACKOFF_FACTOR,
          validateResult: (result) => {
            // Validar que resultado não é null e tem objetos válidos
            if (!result) return false;
            const objetos = result.objetos ?? [];
            return Array.isArray(objetos) && objetos.length > 0;
          },
          abortSignal: options.abortSignal,
          stepName: 'monitorar-objetos-sync',
          pipelineId,
          onRetry: (attempt) => {
            emit('monitor:sync_retry', { attempt });
          }
        }
      );
      
      syncResult = retryResult.result;
      
      sendToTerminal('📥 [PIPELINE:5] Resposta do SYNC recebida', {
        pipelineId,
        hasResult: !!syncResult,
        resultType: syncResult ? typeof syncResult : 'null',
        hasObjetos: !!syncResult?.objetos,
        objetosCount: syncResult?.objetos?.length ?? 0,
        documentoAtivo: syncResult?.documentoAtivo,
        objetosMonitorados: syncResult?.objetosMonitorados,
        attempts: retryResult.attempts
      });
      
      // syncResult agora é MonitorSyncData | null diretamente
      if (syncResult) {
        const objetos = syncResult.objetos ?? [];
        syncedOk = Array.isArray(objetos) && objetos.length > 0;
        syncedObjetos = objetos;
        
        sendToTerminal(syncedOk ? '✅ [PIPELINE:5] SYNC concluído com sucesso' : '❌ [PIPELINE:5] SYNC retornou array vazio', {
          pipelineId,
          objetosCount: syncedObjetos.length,
          primeiroObjeto: syncedObjetos[0] ? {
            nome: syncedObjetos[0].nome,
            staticId: syncedObjetos[0].staticId,
            tipo: syncedObjetos[0].tipo
          } : null,
          attempts: retryResult.attempts
        });
        
        emit(syncedOk ? 'monitor:sync_ok' : 'monitor:sync_error', { count: syncedObjetos.length, attempts: retryResult.attempts });
      } else {
        sendToTerminal('❌ [PIPELINE:5] SYNC retornou NULL após retries', { pipelineId });
        logPipelineError(pipelineId, 'MONITOR_SYNC_FAILED', { 
          syncResult: null 
        });
      }
    } catch (error) {
      sendToTerminal('❌ [PIPELINE:5] Falha ao sincronizar objetos após retries', {
        pipelineId,
        error: error instanceof Error ? error.message : String(error)
      });
      syncResult = null;
      syncedOk = false;
      syncedObjetos = [];
    }

    if (!syncedOk || syncedObjetos.length === 0) {
      sendToTerminal('💥 [PIPELINE:5] SYNC FALHOU - nenhum objeto capturado', {
        pipelineId,
        syncedOk,
        objetosCount: syncedObjetos.length
      });
      logPipelineError(pipelineId, 'MONITOR_SYNC', { syncedOk, objetosCount: syncedObjetos.length });
      const errorMsg = !syncedOk 
        ? 'O monitor não conseguiu capturar o snapshot do documento. O CorelDRAW pode estar ocupado ou instável.'
        : 'Nenhum objeto foi encontrado no documento. Verifique se há objetos válidos no arquivo.';
      return releaseLockAndReturn({ status: 'error', error: { code: 'monitor_sync_failed', message: errorMsg }, metrics: { organizeMs, distributeMs, totalMs: Date.now() - startedAt } });
    }

    // ⏱️ Delay após sync - essencial para CorelDRAW processar snapshot antes de renderizar UI
    sendToTerminal(`⏱️ [PIPELINE:5] Sleep ${PIPELINE_DELAYS.AFTER_MONITOR_SYNC}ms (após sync - estabilização)`, { pipelineId });
    await pipelineSleep(PIPELINE_DELAYS.AFTER_MONITOR_SYNC, 'after_sync');

    // 💾 ETAPA 6: PERSISTIR SNAPSHOT
    sendToTerminal('💾 [PIPELINE:6] Persistindo snapshot...', { 
      pipelineId, 
      objetosCount: syncedObjetos.length,
      docKey: pinnedDocKey 
    });
    
    try {
      // Validação: garantir que syncedObjetos é array válido antes de gravar
      if (!Array.isArray(syncedObjetos)) {
        sendToTerminal('❌ [PIPELINE:6] Validação falhou - syncedObjetos não é array', {
          pipelineId,
          objetosType: typeof syncedObjetos
        });
        logPipelineError(pipelineId, 'SNAPSHOT_VALIDATION', { 
          code: 'invalid_objetos_array', 
          objetosType: typeof syncedObjetos 
        });
        emit('snapshot:error', { reason: 'invalid_objetos_array' });
      } else {
        const payload = { 
          meta: { 
            docKey: pinnedDocKey, 
            capturedAt: Date.now(), 
            total: syncedObjetos.length 
          }, 
          objetos: syncedObjetos 
        };
        writeSnapshot(pipelineId, payload, PIPELINE_TTL.SNAPSHOT_EXTENDED); // ✅ 30s (compatível com SalesOrdersPage)
        
        // Gravar preparedFlag com pipelineId (em vez de '1')
        if (options.pedidoId && options.itemId) {
          setPreparedPipelineId(options.pedidoId, options.itemId, pipelineId);

          // ✅ Gravar flag de distribuição (compatível com SalesOrdersPage)
          try {
            sessionStorage.setItem(`corel:dist:v1:${options.pedidoId}:${options.itemId}`, '1');
          } catch {}

          sendToTerminal('✅ [PIPELINE:6] Snapshot persistido com sucesso', {
            pipelineId,
            pedidoId: options.pedidoId,
            itemId: options.itemId,
            objetosCount: syncedObjetos.length,
            ttlMs: PIPELINE_TTL.SNAPSHOT_EXTENDED
          });
        } else {
          // ⚠️ Sem (pedidoId, itemId) a flag preparada não é gravada e a janela NUNCA hidratará
          // este snapshot. Isso indica chamada com pedido null/incompleto — ver guard em
          // SalesOrdersPage.handleShowDetails.
          sendToTerminal('⚠️ [PIPELINE] Snapshot gravado SEM flag preparada — pedidoId/itemId ausentes', {
            pipelineId,
            pedidoId: options.pedidoId ?? null,
            itemId: options.itemId ?? null,
          });
        }
        emit('snapshot:persisted', { count: syncedObjetos.length });
      }
    } catch (err) {
      sendToTerminal('💥 [PIPELINE:6] Erro ao persistir snapshot', {
        pipelineId,
        error: err instanceof Error ? err.message : String(err)
      });
      logPipelineError(pipelineId, 'SNAPSHOT_PERSIST', { 
        code: 'persist_error', 
        error: err instanceof Error ? err.message : String(err) 
      });
      emit('snapshot:error', { reason: 'persist_error' });
    }
  } catch (err) {
    // Logar erro e bloquear pipeline por falha crítica do monitor
    sendToTerminal('💥 [PIPELINE] EXCEÇÃO no bloco de monitoramento - abortando pipeline', {
      pipelineId,
      error: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined
    });
    logPipelineError(pipelineId, 'MONITOR_BLOCK_EXCEPTION', { 
      error: err instanceof Error ? err.message : String(err),
      note: 'Erro crítico bloqueia pipeline'
    });
    const monitorMs = Date.now() - t4;
    return releaseLockAndReturn({ 
      status: 'error', 
      error: { code: 'monitor_block_exception', message: err instanceof Error ? err.message : 'Erro crítico no bloco de monitoramento' }, 
      metrics: { organizeMs, distributeMs, monitorMs, totalMs: Date.now() - startedAt } 
    });
  }
  const monitorMs = Date.now() - t4;

  // ✅ Gate crítico removido - validação já feita em cada etapa (ETAPA 3 e ETAPA 4)
  // Se chegou aqui, significa que INICIAR e STATUS foram bem-sucedidos

  const totalMs = Date.now() - startedAt;
  sendToTerminal('━━━━━━ [PIPELINE] SUCESSO ━━━━━━', {
    pipelineId,
    monitorInitOk,
    monitorStatusOk,
    docKey: pinnedDocKey,
    metrics: {
      organizeMs,
      distributeMs,
      monitorMs,
      totalMs
    }
  });
  
  logPipelineStage(pipelineId, 'FINISH', { monitorInitOk, monitorStatusOk });
  emit('end', { ok: true });
  return releaseLockAndReturn({ status: 'success', data: { objetos: syncedObjetos }, metrics: { organizeMs, distributeMs, monitorMs, totalMs }, pipelineId, docKey: pinnedDocKey });
}




